import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

const faqs = [
  {
    question: 'Does Brent\'s AMBER DfT rating mean the council will pay my claim?',
    answer: 'Not automatically. AMBER means Brent performs below the best authorities on combined condition, spend and best-practice scorecards — but Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired. Brent\'s own report shows classified roads deteriorated in 2023/24, 12% of unclassified roads need substantial maintenance, and the council projects ~6,000 pothole repairs in 2025/26. Those figures can support a claim when tied to your specific road and defect — they do not guarantee success on their own.',
  },
  {
    question: 'Brent has a GREEN spend rating — can I still claim?',
    answer: 'Yes. The DfT Spend scorecard is GREEN because Brent\'s projected 2025/26 highway maintenance programme totals £4.259m against a DfT capital allocation of £759,000. Section 58 is about the individual defect, not aggregate budgets. Overall and condition ratings are both AMBER, and best practice is RED.',
  },
  {
    question: 'Why is Best Practice RED when Condition is only AMBER?',
    answer: 'The DfT scorecards are separate. Brent\'s transparency report describes AI video surveys and a Well-Managed Highway Infrastructure-aligned approach, but the DfT still rates best practice RED for 2025/26. That gap — documented processes on paper versus the government\'s assessment of practice — is worth citing if Brent relies on generic "we have a system" arguments rather than records for your street.',
  },
  {
    question: 'What if my pothole was on a residential or unclassified road?',
    answer: 'Unclassified roads make up 391.4km — 83% of Brent\'s 469.5km carriageway network. The council\'s latest surveys show 12% of U-roads in RED condition needing substantial maintenance, and the report states unclassified roads "make up 80% of all borough roads." Most Brent pothole damage happens on these streets. Prior reports via FixMyStreet (report.brent.gov.uk) and photos showing defect age matter more than network-wide averages.',
  },
  {
    question: 'Why are pothole repairs projected to rise from 3,998 to ~6,000?',
    answer: 'Brent filled 3,998 potholes in 2024/25 — down from 5,761 in 2023/24 — but estimates ~6,000 repairs in 2025/26 when reactive make-safe work and the injection-patching programme are combined. That projected 50% year-on-year rise is the council\'s own forecast, not an external estimate. It indicates anticipated reactive workload, not proof your specific defect was unavoidable.',
  },
  {
    question: 'What does £0 new injection-patching budget mean for 2025/26?',
    answer: 'Brent\'s 2025/26 capital programme allocates £0 for injection patching, with work funded only by £250,000 budget slippage from previous years — "no additional budget allocated for that year." Injection patching is described as roughly one-third the cost of conventional repairs but with a shorter lifespan. For a claim, the point is whether your road was reasonably maintained — not whether a budget line reads zero — but the slippage-only funding shows prevention running on leftover money.',
  },
  {
    question: 'How do I report a pothole to Brent Council?',
    answer: 'Report defects online at report.brent.gov.uk (FixMyStreet Pro). Brent\'s pothole guidance states high-priority defects are repaired within seven days of inspection, medium within 28 days, and low-priority defects are recorded without further action. Prior reports of the same defect strengthen a claim by demonstrating notice before your incident. Fixtyer searches for existing reports and attaches them to your claim pack.',
  },
];

export function BrentCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Brent Pothole Claims | 6,000 Repairs Projected, RED Best Practice | Fixtyer"
        description="Brent Council earns AMBER DfT ratings on condition but RED on best practice. Pothole repairs projected to rise 50% to ~6,000 in 2025/26 while injection patching gets £0 new budget. What that means for your claim."
        canonical="/councils/brent"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Brent', url: '/councils/brent' },
        ]}
      />
      <FAQSchema faqs={faqs} />
      <ServiceSchema
        councilName="Brent Council"
        councilSlug="brent"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/brent"
        headline="Brent Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`,
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Brent</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="red" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            ~6,000 Pothole Repairs Projected After 3,998 in 2024/25
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Brent earns <strong className="text-amber-400">AMBER</strong> DfT scorecards on overall performance and
            condition, <strong className="text-emerald-400">GREEN</strong> on spend — yet{' '}
            <strong className="text-rose-400">RED</strong> on best practice. Its June 2025 transparency report
            forecasts ~6,000 pothole repairs in 2025/26 (up from 3,998), allocates{' '}
            <strong className="text-rose-400">£0 new budget for injection patching</strong>, and admits classified
            roads deteriorated in 2023/24 despite recent investment. Section 58 still turns on your specific defect.
          </p>

          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">391km</div>
            <div className="text-white font-medium mt-2">Unclassified roads — 83% of the network</div>
            <div className="text-brand-300 text-sm mt-1">
              12% in RED condition at the latest survey, on a £4.5 billion highway asset where the council plans
              7.4 miles of combined resurfacing in 2025/26.
            </div>
          </div>
        </div>

        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`,
          }}
        />
      </section>

      {/* Network scale */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            469.5km of Roads — Mostly Residential
          </h2>
          <p className="text-surface-600 mb-8">
            Network scale from Brent's June 2025 transparency report (Revision A, 30 June 2025)
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">469.5km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">Total carriageway</div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-amber-600">391.4km</div>
              <div className="text-sm text-amber-800 font-medium mt-1">Unclassified (U) roads</div>
              <div className="text-xs text-amber-700 mt-1">83% of the network</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">46.6km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">A roads</div>
            </div>
            <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6">
              <div className="text-3xl font-bold text-brand-900">31.5km</div>
              <div className="text-sm text-surface-700 font-medium mt-1">B and C roads</div>
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-6">
            <table className="w-full text-sm">
              <caption className="sr-only">Brent highway asset breakdown</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Asset</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Footways</td>
                  <td className="px-6 py-4 text-center font-medium">847km</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Bridges and structures</td>
                  <td className="px-6 py-4 text-center font-medium">90 (including 60 bridges)</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Highway gullies</td>
                  <td className="px-6 py-4 text-center font-medium">20,718</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4">Streetlights</td>
                  <td className="px-6 py-4 text-center font-medium">22,848</td>
                </tr>
                <tr>
                  <td className="px-6 py-4">Estimated asset value</td>
                  <td className="px-6 py-4 text-center font-medium">~£4.5 billion</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="To keep on top of the deterioration of our asset, the council must invest continually in maintenance... As time goes on, roads and pavements that are currently in good condition will deteriorate, just like any physical asset such as a house or a vehicle."
            source="Brent Council Local Highways Maintenance Transparency Report (June 2025, Revision A)"
          />
        </div>
      </section>

      {/* Condition data */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Deterioration After Investment
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of condition banding from Brent's own surveys — A and B/C roads surveyed annually
          </p>

          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <p className="text-amber-800 text-sm">
              <strong>Methodology caveat:</strong> Brent's Condition Index measures structural condition — not just
              visible potholes — and RED banding means maintenance "should be considered." The council states that
              from 2026/27 a new BSI PAS 2161 five-category standard will replace the current three-band system.
              Survey results reflect conditions at the time of assessment and may not match today's road surface.
            </p>
          </div>

          {/* A roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A roads (46.6km) — surveyed 100% per year</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">14%</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">83%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center text-rose-600">22%</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">72%</td>
                  </tr>
                  <tr className="border-b border-surface-100 bg-emerald-50">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center text-emerald-600">10%</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">87%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">14%</td>
                    <td className="px-4 py-2 text-center font-bold">3%</td>
                    <td className="px-4 py-2 text-center font-bold">83%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              A-road RED share rose from 10% to 14% between 2023 and 2024 — a 40% relative increase in one year.
              Brent notes A-road performance is still better than 2020/21 and 2021/22, reflecting recent investment,
              but acknowledges deterioration occurred "in spite of increased budget for road resurfacing."
            </p>
          </div>

          {/* B&C roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">B and C roads (31.5km) — surveyed 100% per year</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">11%</td>
                    <td className="px-4 py-2 text-center">4%</td>
                    <td className="px-4 py-2 text-center">85%</td>
                  </tr>
                  <tr className="border-b border-surface-100 bg-emerald-50">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center text-emerald-600">7%</td>
                    <td className="px-4 py-2 text-center">3%</td>
                    <td className="px-4 py-2 text-center">90%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">11%</td>
                    <td className="px-4 py-2 text-center font-bold">5%</td>
                    <td className="px-4 py-2 text-center font-bold">84%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              B/C roads moved from 7% to 11% RED between 2023 and 2024 — a 57% relative increase. The council states
              "capital spend is not the only factor on these figures as at the same time network deterioration also
              affects condition by making it worse."
            </p>
          </div>

          {/* U roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-8">
            <h3 className="font-semibold text-amber-900 mb-4">Unclassified roads (391.4km) — where most claims start</h3>
            <div className="grid sm:grid-cols-3 gap-4 mb-4">
              <div className="bg-white rounded-xl p-4 border border-amber-200 text-center">
                <div className="text-3xl font-bold text-amber-600">12%</div>
                <div className="text-sm text-amber-800 font-medium">RED — substantial maintenance needed</div>
                <div className="text-xs text-amber-700 mt-1">~47km of U-road network</div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-amber-200 text-center">
                <div className="text-3xl font-bold text-brand-900">80%</div>
                <div className="text-sm text-surface-700 font-medium">Share of borough roads</div>
                <div className="text-xs text-surface-500 mt-1">Council's own description</div>
              </div>
              <div className="bg-white rounded-xl p-4 border border-amber-200 text-center">
                <div className="text-3xl font-bold text-brand-900">83%</div>
                <div className="text-sm text-surface-700 font-medium">By carriageway length</div>
                <div className="text-xs text-surface-500 mt-1">391.4km of 469.5km</div>
              </div>
            </div>
            <QuoteCard
              quote="Unclassified roads make up 80% of all borough roads and from the latest surveys and their condition deteriorated slightly, with 12% of Brent's unclassified roads now in need of substantial maintenance. This is in spite of the increased budget for road resurfacing in recent years."
              source="Brent Council Local Highways Maintenance Transparency Report (June 2025, Revision A)"
            />
          </div>
        </div>
      </section>

      {/* Spending and potholes */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold mb-4">
            GREEN Spend, £0 New Prevention Budget
          </h2>
          <p className="text-brand-200 mb-8 max-w-2xl">
            Brent's 2025/26 capital programme and pothole repair projections from the transparency report
          </p>

          <div className="grid sm:grid-cols-3 gap-6 mb-8">
            <div>
              <div className="text-3xl font-bold text-emerald-400">£759k</div>
              <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-emerald-400">£4.259m</div>
              <div className="text-brand-300 text-sm">Total highway maintenance programme 2025/26</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-rose-400">£0</div>
              <div className="text-brand-300 text-sm">New injection-patching budget (slippage only)</div>
            </div>
          </div>

          <div className="bg-brand-900/50 rounded-2xl border border-brand-800 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <caption className="sr-only">Brent 2025/26 capital maintenance programme (£000s)</caption>
              <thead>
                <tr className="bg-brand-800 border-b border-brand-700">
                  <th className="px-4 py-3 text-left font-semibold text-brand-300">Programme</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-300">2025/26 (£000s)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-brand-800">
                <tr>
                  <td className="px-4 py-3">Major resurfacing of roads</td>
                  <td className="px-4 py-3 text-center font-medium">2,425</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">Carriageway short sections</td>
                  <td className="px-4 py-3 text-center">150</td>
                </tr>
                <tr className="bg-rose-900/30">
                  <td className="px-4 py-3">Injection patching</td>
                  <td className="px-4 py-3 text-center font-bold text-rose-400">0</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">Highway structures and drainage</td>
                  <td className="px-4 py-3 text-center">450</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">Condition surveys and analysis</td>
                  <td className="px-4 py-3 text-center">100</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">Renewal of road markings</td>
                  <td className="px-4 py-3 text-center">100</td>
                </tr>
                <tr>
                  <td className="px-4 py-3">Public realm improvements</td>
                  <td className="px-4 py-3 text-center">125</td>
                </tr>
                <tr className="bg-emerald-900/30">
                  <td className="px-4 py-3 font-medium">DfT uplift funding</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-400">759</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="Due to a £250k budget slippage from previous years, this balance will fund work in 2025/26, with no additional budget allocated for that year."
            source="Brent Council Local Highways Maintenance Transparency Report (June 2025, Revision A)"
          />
        </div>
      </section>

      {/* Pothole repairs */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Pothole Repair Volumes
          </h2>
          <p className="text-surface-600 mb-8">
            Estimated potholes filled — from Brent's transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <caption className="sr-only">Brent estimated potholes filled 2020-2026</caption>
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Notes</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">10,210</td>
                  <td className="px-6 py-4 text-center text-surface-500">Peak year in table</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">6,796</td>
                  <td className="px-6 py-4 text-center text-surface-500">—</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">10,718</td>
                  <td className="px-6 py-4 text-center text-surface-500">—</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">5,761</td>
                  <td className="px-6 py-4 text-center text-surface-500">—</td>
                </tr>
                <tr className="border-b border-surface-100 bg-emerald-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-emerald-600">3,998</td>
                  <td className="px-6 py-4 text-center text-emerald-600">Recent low</td>
                </tr>
                <tr className="bg-amber-50">
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2025/26 (projected)</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">~6,000</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-600">+50% vs 2024/25</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What the projection includes</h3>
              <p className="text-surface-600 text-sm">
                Brent estimates ~6,000 pothole repairs in 2025/26 from reactive make-safe work under day-to-day
                maintenance plus the injection-patching programme. That is the council's own forecast — not proof
                every street is failing, but evidence of anticipated reactive workload across the network.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Resurfacing vs backlog</h3>
              <p className="text-amber-800 text-sm">
                Combined 2024/25 and 2025/26 programmes will resurface 38 road sections totalling 7.4 miles, plus
                large-scale patches at 28 DfT-funded locations. At roughly 57km of RED-condition carriageway
                across all road classes at the latest survey, full backlog clearance would take several years even
                without new deterioration — while the council admits deterioration continues during works.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Inspections and innovation */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Surveys, AI Inspections and Section 58
          </h2>
          <p className="text-surface-600 mb-8">
            How Brent says it knows network condition — and where gaps appear for individual claims
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Survey coverage</h3>
              <ul className="text-surface-600 text-sm space-y-2">
                <li>• <strong>A roads:</strong> 100% condition data collected per year</li>
                <li>• <strong>B and C roads:</strong> 100% condition data collected per year</li>
                <li>• <strong>Unclassified roads:</strong> full survey coverage via AI video surveys</li>
                <li>• <strong>TfL principal-road funding:</strong> minimal since 2018; none secured for 2024/26</li>
                <li>• <strong>Resident reports:</strong> logged via Brent's FixMyStreet platform</li>
              </ul>
            </div>
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">AI video surveys</h3>
              <p className="text-surface-600 text-sm mb-3">
                Brent uses AI software on mobile-phone video to scan the borough in little over a week. The council
                states that from borough-wide survey through data analysis to injection-patching works commencing can
                take as little as three weeks.
              </p>
              <p className="text-surface-600 text-sm">
                Faster surveys improve targeting — but a snapshot taken weeks or months before your incident may not
                capture the defect that damaged your vehicle, especially if it formed rapidly after wet weather.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8 mb-8">
            <h3 className="font-display text-xl font-bold mb-4">Section 41 vs Section 58</h3>
            <p className="text-brand-200 mb-4">
              Under <strong className="text-white">Section 41 of the Highways Act 1980</strong>, Brent must maintain
              public highways. To defend a claim under <strong className="text-white">Section 58</strong>, it must
              show a reasonable system for inspecting and repairing the specific defect — not just publish an AMBER
              DfT scorecard or describe AI surveys in a transparency report.
            </p>
            <ul className="text-brand-300 space-y-2 text-sm">
              <li>• Was your road on the latest AI survey before your incident — and did that survey capture this defect?</li>
              <li>• Were there prior reports via report.brent.gov.uk giving actual notice?</li>
              <li>• Does photographic evidence show defect age beyond the council's stated repair timeframe?</li>
              <li>• On a U-road with 12% RED network-wide, can the council show your section was reasonably inspected?</li>
            </ul>
          </div>

          <QuoteCard
            quote="Despite its shorter lifespan than traditional repairs, injection patching offers efficiency and cost advantages—approximately one third the cost of conventional repairs."
            source="Brent Council Local Highways Maintenance Transparency Report (June 2025, Revision A)"
          />
        </div>
      </section>

      {/* Council admissions */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What Brent Acknowledges
          </h2>
          <p className="text-surface-600 mb-8">
            Verbatim admissions from the June 2025 transparency report
          </p>

          <div className="space-y-6">
            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On classified-road deterioration</h3>
              <QuoteCard
                quote="In 23/24, the classified road network had slightly deteriorated in condition... Capital spend is not the only factor on these figures as at the same time network deterioration also affects condition by making it worse."
                source="Brent Council Local Highways Maintenance Transparency Report (June 2025, Revision A)"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On continuous investment</h3>
              <QuoteCard
                quote="To keep on top of the deterioration of our asset, the council must invest continually in maintenance."
                source="Brent Council Local Highways Maintenance Transparency Report (June 2025, Revision A)"
              />
            </div>

            <div>
              <h3 className="font-semibold text-brand-900 mb-3">On A-road performance</h3>
              <QuoteCard
                quote="Although A-road performance is down, it is still better than 20/21 and 21/22 figures and reflects the recent investment put in by Brent."
                source="Brent Council Local Highways Maintenance Transparency Report (June 2025, Revision A)"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Balanced claims section */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against an AMBER-Rated Borough
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Brent invests beyond its DfT allocation — but condition and best practice tell a mixed story
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-surface-50 rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What works in the council's favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — £4.259m programme vs £759k DfT allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> AI video surveys with rapid defect-to-repair turnaround described
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> £15m four-year footway and carriageway programme largely completing 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Pothole fills fell to 3,998 in 2024/25 before the 2025/26 projection
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Well-Managed Highway Infrastructure-aligned asset management approach documented
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence citing surveys, FixMyStreet logs and repair records.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What works in yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> RED best-practice scorecard from the DfT
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Classified roads deteriorated in 2023/24 despite increased resurfacing spend
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 12% of U-roads in RED — ~47km needing substantial maintenance
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> ~6,000 pothole repairs projected for 2025/26 — 50% above 2024/25
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> £0 new injection-patching budget — prevention funded by slippage only
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> No TfL principal-road funding secured for 2024/26
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The winning strategy here is specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a borough with GREEN spend and AI survey capability, your claim lives or dies on the{' '}
              <strong className="text-white">specific defect and road</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole on report.brent.gov.uk — proof of actual notice</li>
              <li>• Photos showing defect size, depth and visible age (weathered edges, previous patching)</li>
              <li>• Road class — on a U-road, network-wide 12% RED condition supports structural context</li>
              <li>• Timing — whether the defect could have formed after the last AI survey pass</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: prior-report search, photo assessment, and citations from Brent's own
              transparency data — including the 2025/26 pothole projection and injection-patching budget gap — where
              they help you.
            </p>
          </div>
        </div>
      </section>

      {/* Report a pothole CTA */}
      <section className="py-12 bg-surface-100 border-y border-surface-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-3">
            Report a Pothole to Brent Council
          </h2>
          <p className="text-surface-600 mb-6">
            Reporting a defect creates a record the council had notice. Do this before claiming — and tell us when
            you reported it so we can reference it in your pack. High-priority defects are repaired within seven
            days of inspection per council guidance.
          </p>
          <a
            href="https://report.brent.gov.uk/"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-6 py-3 bg-brand-600 text-white font-semibold rounded-xl hover:bg-brand-700 transition-colors"
          >
            Report a street problem — report.brent.gov.uk
            <svg className="ml-2 w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </a>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Brent?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            An AMBER borough with RED best practice demands a precise claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road deterioration context</li>
                <li>• No prior-report search</li>
                <li>• No RED best-practice citation</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional claim pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 12% U-road RED condition documented</li>
                <li>✅ ~6,000 pothole projection for 2025/26 cited</li>
                <li>✅ £0 injection-patching budget quoted</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Brent</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=brent"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Brent Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View all council ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently asked questions
          </h2>

          <div className="space-y-6">
            {faqs.map((faq) => (
              <div key={faq.question} className="bg-white rounded-xl p-6 border border-surface-200">
                <h3 className="font-semibold text-brand-900 mb-2">{faq.question}</h3>
                <p className="text-surface-600 text-sm">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources:{' '}
            <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a>{' '}
            |{' '}
            <a
              href="https://www.brent.gov.uk/-/media/files/resident-documents/parking-roads-and-transport-document/local-highways-maintenance-transparency-report.pdf?rev=db109e86407e44649cec758c6c5cc030"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Brent Council Local Highways Maintenance Transparency Report (June 2025, Revision A)
            </a>
            . Contains public sector information licensed under the{' '}
            <a
              href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Open Government Licence v3.0
            </a>
            .
          </p>
        </div>
      </section>
    </div>
  );
}
